import React from 'react'
import { AlertTriangle, RefreshCw, Hexagon } from 'lucide-react'

interface Props {
  children: React.ReactNode
  fallback?: React.ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  errorId: string
}

function generateErrorId(): string {
  return `ARK-ERR-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`
}

export default class AppErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null, errorId: '' }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorId: generateErrorId() }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Internal console-only logging — never exposed to public UI
    // eslint-disable-next-line no-console
    console.error('[Ark ErrorBoundary]', this.state.errorId, error, errorInfo)
  }

  private handleReload = () => {
    window.location.reload()
  }

  private handleGoHome = () => {
    window.location.href = '/'
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div className="min-h-screen bg-ark-void text-ark-text flex items-center justify-center p-6">
          <div className="max-w-md w-full space-y-6">
            {/* Brand */}
            <div className="flex items-center justify-center gap-3">
              <div className="relative">
                <Hexagon className="w-8 h-8 text-ark-gold" strokeWidth={1.5} />
              </div>
              <span className="text-lg font-semibold text-ark-text-bright tracking-tight">ARK</span>
            </div>

            {/* Error Card */}
            <div className="ark-card border-ark-danger/40 bg-ark-danger/5 space-y-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-ark-danger shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <h1 className="ark-heading-md text-ark-danger">
                    Surface Error
                  </h1>
                  <p className="text-sm text-ark-text-dim">
                    An error occurred while rendering this surface. The system has contained the failure and is displaying a safe fallback.
                  </p>
                </div>
              </div>

              {/* Error ID — for internal support only, not a stack trace */}
              <div className="bg-ark-panel/50 rounded-ark-sm px-3 py-2">
                <div className="text-2xs font-mono uppercase tracking-wider text-ark-text-muted mb-1">
                  Error Reference
                </div>
                <div className="font-mono text-xs text-ark-text-dim select-all">
                  {this.state.errorId}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-3">
                <button
                  onClick={this.handleReload}
                  className="flex items-center gap-2 px-4 py-2 bg-ark-gold/10 text-ark-gold text-sm font-medium rounded-ark-sm hover:bg-ark-gold/20 transition-ark border border-ark-gold/30"
                >
                  <RefreshCw className="w-4 h-4" />
                  Reload Surface
                </button>
                <button
                  onClick={this.handleGoHome}
                  className="px-4 py-2 text-sm text-ark-text-dim hover:text-ark-text transition-ark"
                >
                  Return to Ark
                </button>
              </div>

              {/* Footer */}
              <p className="text-2xs text-ark-text-muted">
                No source code or stack traces are exposed in public builds. Report the error reference above to the system administrator.
              </p>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
